import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, Truck, CheckCircle, Clock, Search, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Input from '@/components/ui/Input';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface TrackingData {
  order_number: string;
  status: string;
  created_at: string;
  shipped_at: string | null;
  delivered_at: string | null;
  shipments: Array<{
    id: string;
    carrier: string;
    service_name: string;
    tracking_number: string;
    tracking_url: string;
    tracking_status: string;
    status: string;
    shipped_at: string | null;
    delivered_at: string | null;
  }>;
  items: Array<{
    product_name: string;
    variant_name: string | null;
    quantity: number;
    image_url: string | null;
  }>;
  timeline: Array<{ status: string; notes: string; created_at: string }>;
}

const STATUS_BADGES: Record<string, 'yellow' | 'blue' | 'green' | 'red' | 'gray'> = {
  pending: 'yellow',
  processing: 'blue',
  shipped: 'blue',
  delivered: 'green',
  cancelled: 'red',
  refunded: 'gray',
};

function getStepIndex(status: string): number {
  const map: Record<string, number> = { pending: 0, processing: 1, shipped: 2, delivered: 3 };
  return map[status] ?? -1;
}

export default function TrackOrder() {
  const { t } = useTranslation('tracking');
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const [tracking, setTracking] = useState<TrackingData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lookup form
  const [lookupOrder, setLookupOrder] = useState('');
  const [lookupEmail, setLookupEmail] = useState('');

  const timelineSteps = useMemo(
    () => [
      { key: 'pending', label: t('progress.orderPlaced'), icon: Clock },
      { key: 'processing', label: t('progress.processing'), icon: Package },
      { key: 'shipped', label: t('progress.shipped'), icon: Truck },
      { key: 'delivered', label: t('progress.delivered'), icon: CheckCircle },
    ],
    [t],
  );

  useEffect(() => {
    if (orderNumber) fetchTracking(orderNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  async function fetchTracking(num: string) {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<TrackingData>(`/tracking/${num}`);
      setTracking(data);
    } catch {
      setError(t('lookup.errors.notFound'));
    } finally { setIsLoading(false); }
  }

  async function handleLookup(e: React.FormEvent) {
    e.preventDefault();
    if (!lookupOrder || !lookupEmail) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.post<TrackingData>('/tracking/lookup', {
        order_number: lookupOrder,
        email: lookupEmail,
      });
      setTracking(data);
    } catch {
      setError(t('lookup.errors.notFoundWithEmail'));
    } finally { setIsLoading(false); }
  }

  function statusLabel(status: string): string {
    const key = `statuses.${status}` as const;
    const translated = t(key);
    if (translated === key) {
      // Unknown status — fall back to capitalized raw string
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
    return translated;
  }

  const currentStep = tracking ? getStepIndex(tracking.status) : -1;
  const isCancelled = tracking?.status === 'cancelled' || tracking?.status === 'refunded';

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-display font-bold text-stone-900 mb-2">{t('title')}</h1>
        <p className="text-stone-500">{t('subtitle')}</p>
      </div>

      {/* Lookup Form (show when no tracking data) */}
      {!tracking && !isLoading && (
        <Card>
          <form onSubmit={handleLookup} className="space-y-4">
            <Input label={t('lookup.orderNumberLabel')} value={lookupOrder} onChange={e => setLookupOrder(e.target.value)}
              placeholder={t('lookup.orderNumberPlaceholder')} />
            <Input label={t('lookup.emailLabel')} type="email" value={lookupEmail} onChange={e => setLookupEmail(e.target.value)}
              placeholder={t('lookup.emailPlaceholder')} />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button type="submit" variant="primary" className="w-full" disabled={!lookupOrder || !lookupEmail}>
              <Search className="h-4 w-4 mr-2" />{t('lookup.submit')}
            </Button>
          </form>
        </Card>
      )}

      {isLoading && <div className="flex justify-center py-12"><LoadingSpinner /></div>}

      {tracking && (
        <div className="space-y-6">
          {/* Order Header */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-stone-900">{t('details.heading', { number: tracking.order_number })}</h2>
                <p className="text-sm text-stone-500">{t('details.placedOn', { date: formatDate(tracking.created_at) })}</p>
              </div>
              <Badge variant={STATUS_BADGES[tracking.status] || 'gray'}>
                {statusLabel(tracking.status)}
              </Badge>
            </div>

            {/* Progress Timeline */}
            {!isCancelled && (
              <div className="flex items-center justify-between mt-6" role="list" aria-label={t('details.progressAriaLabel')}>
                {timelineSteps.map((step, index) => {
                  const Icon = step.icon;
                  const isCompleted = index <= currentStep;
                  const isCurrent = index === currentStep;
                  return (
                    <div key={step.key} className="flex flex-col items-center flex-1 relative" role="listitem"
                      aria-current={isCurrent ? 'step' : undefined}>
                      {index > 0 && (
                        <div className={`absolute top-5 right-1/2 w-full h-0.5 -translate-y-1/2 ${
                          index <= currentStep ? 'bg-sage-500' : 'bg-stone-200'}`} aria-hidden="true" />
                      )}
                      <div className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full ${
                        isCompleted ? 'bg-sage-100 text-sage-700' : 'bg-stone-100 text-stone-400'}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className={`text-xs mt-2 text-center ${isCompleted ? 'font-medium text-sage-700' : 'text-stone-400'}`}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Shipment Cards */}
          {tracking.shipments.length > 0 && (
            <div className="space-y-3">
              {tracking.shipments.map(shipment => (
                <Card key={shipment.id}>
                  <div className="flex items-start gap-3">
                    <Truck className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-stone-900 uppercase">{shipment.carrier}</span>
                        <span className="text-xs text-stone-500">{shipment.service_name}</span>
                      </div>
                      {shipment.tracking_number && (
                        <p className="text-sm text-stone-600">
                          {t('details.trackingLabel')} <span className="font-mono">{shipment.tracking_number}</span>
                          {shipment.tracking_url && (
                            <a href={shipment.tracking_url} target="_blank" rel="noopener"
                              className="inline-flex items-center gap-1 ml-2 text-blue-600 hover:underline">
                              {t('details.trackOnCarrier')} <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </p>
                      )}
                      {shipment.tracking_status && (
                        <p className="text-xs text-stone-500 mt-1">{t('details.statusPrefix')} {shipment.tracking_status}</p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Items */}
          <Card title={t('details.items')}>
            <div className="divide-y divide-stone-100">
              {tracking.items.map((item, i) => (
                <div key={i} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.product_name}
                      className="w-12 h-12 object-cover rounded border border-stone-200" />
                  ) : (
                    <div className="w-12 h-12 bg-stone-100 rounded flex items-center justify-center">
                      <Package className="h-5 w-5 text-stone-300" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-stone-900">{item.product_name}</p>
                    {item.variant_name && <p className="text-xs text-stone-500">{item.variant_name}</p>}
                    <p className="text-xs text-stone-400">{t('details.qty', { quantity: item.quantity })}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Timeline */}
          {tracking.timeline.length > 0 && (
            <Card title={t('details.timelineHeading')}>
              <div className="space-y-3">
                {tracking.timeline.map((event, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 rounded-full bg-sage-500 mt-1.5" />
                      {i < tracking.timeline.length - 1 && <div className="w-0.5 flex-1 bg-stone-200 mt-1" />}
                    </div>
                    <div className="pb-3">
                      <p className="text-sm text-stone-900">{event.notes || statusLabel(event.status)}</p>
                      <p className="text-xs text-stone-400">{formatDate(event.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Back button */}
          <div className="text-center">
            <button onClick={() => { setTracking(null); setError(null); }}
              className="text-sm text-sage-700 hover:underline">
              {t('details.trackAnother')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
